import {
   applyTransaction,
   arrayAdd,
   arrayRemove,
   arrayUpdate,
   arrayUpsert,
   combineQueries,
   type EntityState,
   type EntityStore,
   filterNilValue,
   getEntityStoreByName,
   type getEntityType,
   type getIDType,
   type PaginationResponse,
   PaginatorPlugin,
   type QueryEntity,
   type UpdateEntityPredicate,
} from '@datorama/akita';
import { type SetEntities } from '@datorama/akita/src/lib/setEntities';
import {
   type GridDataSource,
   type GridFilterModel,
   type GridGetRowsParams,
   type GridPaginationModel,
   type GridSortModel,
} from '@mui/x-data-grid-premium/';
import { type AxiosError } from 'axios';
import { formatISO, isDate, isSameDay, isValid, parseISO } from 'date-fns';
import { diff } from 'deep-object-diff';
import { isEqual, isNil, isObject, isPlainObject, omit, pick } from 'lodash-es';
import {
   BehaviorSubject,
   combineLatest,
   distinctUntilChanged,
   EMPTY,
   EmptyError,
   firstValueFrom,
   from,
   lastValueFrom,
   map,
   type Observable,
   of,
   share,
   type Subscription,
   switchMap,
   tap,
} from 'rxjs';
import { includeSubsidiaries$, locale$, pageSize$, userContextQuery } from './../context/UserContext/query';
import { type FailedAuditRequest, markAuditFailureReported } from './auditResult';
import HistoryService from './History/service';
import { type default as KPIFieldsStore } from './KPIField/store';
import { type default as KPIScopesStore } from './KPIScope/store';
import { type default as KPIValuesStore } from './KPIValue/store';
import { activeEntityId$ } from './ReportingStandards/query';
import { activeOrganisationId$ } from 'context/OrganisationContext/query';
import { context$ as periodContext$ } from 'context/PeriodContext/query';
import { type UserContextState } from 'context/UserContext/store';
import {
   type Attachment,
   type ErrorObject as ErrorType,
   type FootprintSub,
   type ISO8601DateOnlyString,
   type KpiScope,
   type Organisation,
   type OrganisationAssignment,
   type Pagination,
   type TaxonomyActivityAssignment,
   type User,
} from 'types/models';
import axiosClient from 'utils/axiosAPIClient';
import { ApprovalStatus, type StatusVerificationEnumExtended, type StatusVerificationTypeEnum } from 'utils/enum';
import { handleError } from 'utils/errorHandler';

function determineAuditActionEntityPathSegmentAndParameters<T>({
   entityName,
   entityIdOrStandard,
   modelName,
   params,
   includeSubsidiaries,
   forceAudit = false,
}: {
   entityName: string;
   entityIdOrStandard: string | getIDType<T>;
   modelName?: string;
   params?: string | URLSearchParams;
   includeSubsidiaries?: boolean;
   forceAudit?: boolean;
}): { path: string; queryParams: URLSearchParams } {
   let path = entityName;

   const queryParams = new URLSearchParams(params);
   queryParams.set('force', String(forceAudit));
   if (includeSubsidiaries !== undefined) {
      queryParams.set('includeSubsidiaries', String(includeSubsidiaries));
   }

   if (Number.isInteger(Number.parseInt(entityIdOrStandard as string, 10)) && !modelName) {
      // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
      path = `${entityName}/${entityIdOrStandard}`;
   } else if (entityName === 'kpiscopes') {
      // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
      path = `scoping/${entityIdOrStandard}`;
   } else if (entityName === 'taxonomy/minimumsafeguards') {
      path = 'taxonomy/minimumsafeguards';
   } else if (entityName === 'taxonomy/fslis') {
      path = 'taxonomy/fslis';
   } else if (modelName) {
      // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
      path = `${modelName}/${entityIdOrStandard}`;
      // For currency rates, the identifier is a date that needs formatting
   } else if (entityName === 'currenciesrates') {
      if (isValid(parseISO(entityIdOrStandard as string))) {
         const dateValue = parseISO(entityIdOrStandard as string);
         path = `${entityName}/${formatISO(dateValue, { representation: 'date' })}`;
      } else if (typeof entityIdOrStandard === 'string') {
         const [currencyTo, currencyFrom, currencyDate] = entityIdOrStandard.split('_');

         if (currencyFrom && currencyTo) {
            queryParams.set('currencyFrom', currencyFrom);
            queryParams.set('currencyTo', currencyTo);
         }
         if (currencyDate) {
            queryParams.set('currencyDate', currencyDate);
         }
      }
   }

   return { path, queryParams };
}

type SetStatusValues = {
   status: StatusVerificationEnumExtended;
   reviewer?: Partial<UserContextState> | null;
   approver?: Partial<UserContextState> | null;
   reviewerId?: number | null;
   approverId?: number | null;
};

interface CRUDServiceOptions {
   collections?: string[] | Record<string, string>;
   useOrganisationContext?: boolean;
   usePeriodContext?: boolean;
   useScope?: boolean;
   includeSubsidiaries?: boolean;
   paginate?: boolean;
   useReportingStandardContext?: boolean;
}

export interface QueryDependency {
   observable: Observable<any>;
   key: string;
}
export type QueryResolvedValues = Record<string, any>;

/** Paginated response from Approval Center and similar list endpoints */
export interface PaginatedEntityResponse<T> {
   items: T[];
   total: number;
   perPage: number;
   currentPage: number;
   lastPage: number;
}

/**
 * EntityResponse<T>
 * Accepts multiple shapes:
 * - Array of entities
 * - Object with items array
 * - Paginated response with items
 * - Object with data array
 * (This union covers responses from various endpoints.)
 */
export type EntityResponse<T> =
   | getEntityType<T>[]
   | { items: getEntityType<T>[] }
   | (PaginatedEntityResponse<getEntityType<T>> & { items: getEntityType<T>[] })
   | { data: getEntityType<T>[] }
   | PaginationResponse<getEntityType<T>>;

function getScopeFilterFunction(from: Date, to: Date, organisationId: number) {
   return ({ materialityType, from: scopeFrom, to: scopeTo, organisationId: scopeOrganisationId }: KpiScope) =>
      isSameDay(from, parseISO(scopeFrom)) && isSameDay(to, parseISO(scopeTo)) && scopeOrganisationId === organisationId && !!materialityType;
}

export default abstract class CRUDService<T extends EntityState> {
   static apiVersion = 'v2';
   protected httpClient: typeof axiosClient;

   private isInitial: boolean;
   private isLoading: boolean;
   // setActiveEntity still activates an entity whose load failed, so a later call for it must load again instead of reporting success.
   private failedLoadId?: getIDType<T>;
   protected entityName: string;
   protected store: EntityStore<T>;
   protected query: QueryEntity<T>;
   protected version: string;
   protected collections: CRUDServiceOptions['collections'];
   protected useOrganisationContext: CRUDServiceOptions['useOrganisationContext'];
   protected usePeriodContext: CRUDServiceOptions['usePeriodContext'];
   protected useReportingStandardContext: CRUDServiceOptions['useReportingStandardContext'];
   protected useScope: CRUDServiceOptions['useScope'];
   protected includeSubsidiaries: CRUDServiceOptions['includeSubsidiaries'];
   protected paginate: CRUDServiceOptions['paginate'];
   protected hasPageChangeObservable: boolean;
   protected userContextQuery: typeof userContextQuery;
   protected pageQueryParams?: string;
   queryParams?: string;
   protected customQueryParams: any;
   protected queryParamsObservable: Observable<any>;
   private _setParams: any;
   paginator?: PaginatorPlugin<any>;
   protected pageChanges$?: BehaviorSubject<any>;
   protected getEntitiesSubscription?: Subscription;
   private historyService: HistoryService;
   // The store that reports failed audit actions, when `store` is not in the global error collector (`state/Error/query.ts`).
   protected auditErrorStore?: EntityStore<any>;
   pagination$?: Observable<Pagination<getEntityType<T>>>;

   requests: RequestsManager;
   subscriptions: SubscriptionsManager;

   // implemented by child classes
   kpiValuesStore?: typeof KPIValuesStore;
   kpiFieldsStore?: typeof KPIFieldsStore;
   kpiScopesStore?: typeof KPIScopesStore;
   refreshCurrentKPIProgress?(): Promise<void>;
   setStatus?(
      _organisationId: Organisation['organisationId'],
      _from: Date,
      _to: Date,
      _setStatusValues: SetStatusValues,
      _auditValues: getIDType<T>[]
   ): void;
   setStatusClimateRisk?(_setStatusValues: SetStatusValues): void;
   setStatusApprovalCenter?(_setStatusValues: SetStatusValues, _auditValues: getIDType<T>[], _entityName?: string): void;

   constructor(
      entityName: string,
      store: EntityStore<T>,
      query: QueryEntity<T>,
      {
         collections = [],
         useOrganisationContext = false,
         usePeriodContext = false,
         useReportingStandardContext = false,
         useScope = undefined,
         includeSubsidiaries = undefined,
         paginate = false,
      }: CRUDServiceOptions = {}
   ) {
      this.isInitial = true;
      this.isLoading = false;
      this.entityName = entityName;
      this.store = store;
      this.query = query;
      this.version = CRUDService.apiVersion;
      this.collections = collections;
      this.useOrganisationContext = useOrganisationContext;
      this.usePeriodContext = usePeriodContext;
      this.useReportingStandardContext = useReportingStandardContext;
      this.useScope = useScope;
      this.includeSubsidiaries = includeSubsidiaries;
      this.paginate = paginate;
      this.httpClient = axiosClient;
      this.hasPageChangeObservable = false;
      this.userContextQuery = userContextQuery;
      this.historyService = new HistoryService();
      this.pageQueryParams = undefined;
      this.queryParams = undefined;
      this.customQueryParams = undefined;

      this.queryParamsObservable = this.getQueryParams();

      this._setParams = this.getQueryParams().subscribe();

      this.requests = new RequestsManager(this.entityName);
      this.subscriptions = new SubscriptionsManager();

      if (this.paginate) {
         this.paginator = new PaginatorPlugin(this.query, {
            clearStoreWithCache: false,
         });
         this.pageChanges$ = new BehaviorSubject(EMPTY);
         this.pagination$ = this.pageChanges$.pipe(
            filterNilValue(),
            switchMap((source) => source)
         ) as Observable<Pagination>;
      }

      locale$.pipe(filterNilValue()).subscribe((locale) => {
         this.setRequestLocale(locale);
      });
   }

   appendDataToStore(data: Partial<T>, store = this.store) {
      store.update((prevState) => ({
         ...prevState,
         ...data,
      }));
   }

   getPageChangesObservable() {
      return this.paginator?.pageChanges.pipe(filterNilValue(), distinctUntilChanged());
   }

   getPageChangesCombinedObservable(additionalQueryParams?: Record<string, string>): Observable<Pagination | undefined> {
      return combineLatest([this.getPageChangesObservable(), this.getPageParams(), this.getQueryParams()]).pipe(
         distinctUntilChanged(isEqual),
         switchMap((val): Observable<any> => {
            let page;
            let pageQueryParams;
            let generalQueryParams;

            if (Array.isArray(val)) {
               [page, pageQueryParams, generalQueryParams] = val;
            } else {
               page = this?.paginator?.currentPage ?? 1;
               pageQueryParams = this.pageQueryParams;
               generalQueryParams = this.queryParams;
            }

            const pageParams = new URLSearchParams(pageQueryParams);
            const queryParams = new URLSearchParams(generalQueryParams);

            const additionalParams = new URLSearchParams(additionalQueryParams);

            const finalParams = new URLSearchParams([...queryParams, ...additionalParams]);

            const resetPage =
               Object.entries(Object.fromEntries(pageParams)).some(
                  ([key, value]) => !this.paginator?.metadata.has(key) || this.paginator?.metadata.get(key) !== value
               ) || this?.paginator?.metadata?.size === 0;

            const thePage = resetPage ? 1 : page;

            if (resetPage && this.paginator) {
               this.paginator.clearPage(1);
            }

            pageParams.forEach((value, key) => this.paginator?.metadata.set(key, value));

            // Explanation: when we PATCH an entity, we always received the updated entity from the response. I think it does not make sense to
            // load the page again. For these cases, this.paginator.getQuery().getAll() is a hack to tell the paginator "this is the new data"
            // because otherwise I found it to be empty (if I don't provide that) or it will call the page again, which costs resources
            if (!this?.paginator?.getPage) return of(undefined);
            if (this.isLoading || this.query.hasActive()) {
               // eslint-disable-next-line @typescript-eslint/dot-notation
               const pagination = this.paginator['pagination'];
               return this.paginator.getPage(() => of({ ...pagination, data: this.paginator?.getQuery().getAll() }));
            }
            return this.paginator.getPage(() => from(this.getPage(thePage, pageParams, finalParams)));
         }),
         share()
      );
   }

   getPageParams() {
      const dependencies = [];

      dependencies.push(pageSize$);

      let urlParams;

      if (!dependencies.length) {
         urlParams = new URLSearchParams();
         const urlParamsString = urlParams.toString();
         this.pageQueryParams = `?${urlParams.toString()}`;
         return of(urlParamsString ? this.pageQueryParams : undefined);
      }

      return combineQueries(dependencies).pipe(
         distinctUntilChanged(isEqual),
         map(([pageSize]) => {
            urlParams = new URLSearchParams();

            if (pageSize && pageSize !== 0) {
               urlParams.set('limit', pageSize);
            }

            const urlParamsString = urlParams.toString();
            return urlParamsString ? `?${urlParams.toString()}` : undefined;
         }),
         distinctUntilChanged(),
         tap((pageQueryParams) => {
            if (this.pageQueryParams && this.pageQueryParams !== pageQueryParams && this.paginate && this?.paginator?.hasPage(1)) {
               this.paginator?.clearCache();
               this.paginator?.clearCache();
            }

            this.pageQueryParams = pageQueryParams;
         })
      );
   }

   getQueryParams() {
      const dependencies: QueryDependency[] = [];

      if (this.useOrganisationContext) {
         dependencies.push({ observable: activeOrganisationId$.pipe(filterNilValue()), key: 'organisationId' });
      }
      if (this.usePeriodContext) {
         dependencies.push({ observable: periodContext$.pipe(filterNilValue()), key: 'period' });
      }
      if (this.useReportingStandardContext) {
         dependencies.push({ observable: activeEntityId$.pipe(filterNilValue()), key: 'reportingStandardId' });
      }
      if (this.includeSubsidiaries) {
         dependencies.push({ observable: includeSubsidiaries$.pipe(filterNilValue()), key: 'includeSubsidiaries' });
      }

      if (!dependencies.length) {
         const urlParams = new URLSearchParams();
         urlParams.set('scope', this.useScope === true ? 'true' : this.useScope === false ? 'false' : 'any');
         this.queryParams = `?${urlParams.toString()}`;
         return of(this.queryParams);
      }

      return combineLatest(dependencies.map((d) => d.observable)).pipe(
         distinctUntilChanged(isEqual),
         map((values: any[]) => {
            const resolvedValues = values.reduce((acc: QueryResolvedValues, value, index) => {
               acc[dependencies[index].key] = value;
               return acc;
            }, {});

            const urlParams = new URLSearchParams(this.customQueryParams);

            urlParams.set('scope', this.useScope === true ? 'true' : this.useScope === false ? 'false' : 'any');

            if (resolvedValues.organisationId) {
               urlParams.set('organisationId', resolvedValues.organisationId);
            }
            if (resolvedValues.reportingStandardId) {
               urlParams.set('reportingStandardId', resolvedValues.reportingStandardId);
            }
            if (resolvedValues.period) {
               urlParams.set('from', resolvedValues.period.from);
               urlParams.set('to', resolvedValues.period.to);
            }
            if (resolvedValues.includeSubsidiaries !== undefined) {
               urlParams.set('includeSubsidiaries', String(resolvedValues.includeSubsidiaries));
            }

            // Ensure all required values are present before returning query params
            if (
               (!this.useOrganisationContext || resolvedValues.organisationId) &&
               (!this.usePeriodContext || resolvedValues.period) &&
               (!this.useReportingStandardContext || resolvedValues.reportingStandardId) &&
               (!this.includeSubsidiaries || resolvedValues.includeSubsidiaries !== undefined)
            ) {
               return `?${urlParams.toString()}`;
            }

            return undefined; // Incomplete params
         }),
         distinctUntilChanged(),
         tap((queryParams) => {
            if (this.queryParams && this.queryParams !== queryParams && this.paginate && this?.paginator?.hasPage(1)) {
               this.paginator?.clearCache({ clearStore: true });
               this.paginator?.clearCache({ clearStore: true });
               this.paginator?.setPage(1);
            }

            this.queryParams = queryParams;
         })
      );
   }

   setError<T extends EntityState<any, any> = typeof this.store extends EntityStore<infer U, any, any> ? U : never>(
      error: ErrorType,
      store?: EntityStore<T, getEntityType<T>, getIDType<T>>
   ) {
      handleError(error, store ?? this.store);
   }

   getLoading() {
      return this.query.getValue().loading;
   }

   getError() {
      return this.query.getValue().error;
   }

   async getPage(
      page = 1, // API page starts at 1
      pageQueryParams: string | URLSearchParams = '',
      generalQueryParams: string | URLSearchParams = '',
      entityName = this.entityName
   ) {
      const queryParams = new URLSearchParams(generalQueryParams);
      const additionalParams = new URLSearchParams(pageQueryParams);

      additionalParams.forEach((value, key) => queryParams.set(key, value));

      // an offset already addresses the rows exactly; a page next to it would only contradict it
      if (queryParams.has('offset')) {
         queryParams.delete('page');
      } else {
         queryParams.set('page', String(page));
      }

      return this.httpClient
         .get<PaginationResponse<getEntityType<T>>>(`/${this.version}/${entityName}?${queryParams.toString()}`)
         .then((resp) => resp.data)
         .catch((error) => {
            this.setError(error);
            return { data: [], total: 0 } as unknown as PaginationResponse<getEntityType<T>>;
         });
   }

   async getEntities({
      force = false,
      store = this.store,
      url,
      subscriptionKey = 'getEntities',
      additionalQueryParams,
   }: {
      force?: boolean;
      store?: EntityStore<T>;
      url?: string;
      subscriptionKey?: string;
      additionalQueryParams?: Record<string, string>;
      [key: string]: any;
   } = {}): Promise<any> {
      if (this.paginate) {
         try {
            if (this.hasPageChangeObservable) {
               if (this?.paginator?.currentPage && this.paginator.hasPage(this.paginator.currentPage) && !force) {
                  return this.paginator.refreshCurrentPage();
               }

               return this.pageChanges$?.next(this.getPageChangesCombinedObservable(additionalQueryParams));
            }
            if (this.pageChanges$ && !this.pageChanges$.closed) {
               const innerObservable$ = this.pageChanges$.value;
               await lastValueFrom(innerObservable$);
            } else {
               this.pageChanges$ = new BehaviorSubject(EMPTY);
               this.pagination$ = this.pageChanges$.pipe(
                  filterNilValue(),
                  switchMap((source) => source)
               ) as Observable<Pagination>;
            }
         } catch (error: any) {
            if (error instanceof EmptyError) {
               if (!this.hasPageChangeObservable) {
                  this.hasPageChangeObservable = true;
                  return this.pageChanges$?.next(this.getPageChangesCombinedObservable(additionalQueryParams));
               }
               return undefined;
            }
            throw new Error(error);
         }
      }

      if (force && this.subscriptions.has(subscriptionKey)) {
         this.subscriptions.unsubscribe(subscriptionKey);
      }

      if (!this.subscriptions.has(subscriptionKey)) {
         const subscription = this.queryParamsObservable
            .pipe(
               tap((queryString) => {
                  const queryParams = new URLSearchParams(queryString);
                  const additionalParams = new URLSearchParams(additionalQueryParams);

                  const finalParams = new URLSearchParams([...queryParams, ...additionalParams]);

                  void this.fetchEntities(`?${finalParams.toString()}`, store, url);
               })
            )
            .subscribe();
         this.subscriptions.add(subscriptionKey, subscription);
      }
   }

   private async fetchEntities(queryString: string | undefined, store: EntityStore<T> = this.store, url?: string): Promise<void> {
      if (!queryString) return;

      store.setLoading(true);

      return applyTransaction(async () => {
         try {
            if (this.query.hasActive()) {
               const activeId = this.query.getActiveId();
               if (activeId !== undefined) {
                  void this.loadCollections(activeId);
               }
            }

            // Create new controller (abort old if different queryString)
            const signal = this.requests.add(queryString);
            if (!signal) return; // exact same request already running

            const requestURL = url ?? `/${this.entityName}`;
            const response = await this.httpClient.get<EntityResponse<T>>(`/${this.version}${requestURL}${queryString}`, { signal });
            this.processEntitiesResponse(response.data, store);
            // A successful load clears any prior (e.g. transient gateway) error so
            // recoverable error UIs and stale snackbars resolve after a retry.
            store.setError(null);
         } catch (error) {
            if (!((error as AxiosError)?.name === 'AbortError' || (error as AxiosError)?.code === 'ERR_CANCELED')) {
               this.setError(error as AxiosError, store);
            }
         } finally {
            this.requests.remove(queryString);
            store.setLoading(false);
         }
      });
   }

   private processEntitiesResponse(data: EntityResponse<T>, store: EntityStore<T>): SetEntities<getEntityType<T>> {
      // Support: plain array, { items }, or { data } (PaginationResponse-style)
      let items: getEntityType<T>[] | null = null;
      if (Array.isArray(data)) {
         items = data;
      } else if ('items' in data && Array.isArray((data as { items?: unknown }).items)) {
         items = data.items;
      } else if ('data' in data && Array.isArray((data as { data?: unknown }).data)) {
         items = data.data;
      }

      if (!items) {
         store.set(data as SetEntities<getEntityType<T>>);
         return data as SetEntities<getEntityType<T>>;
      }

      if (this.query.hasActive() && items.some((item) => item[store.idKey] === this.query.getActiveId())) {
         const activeItem = items.find((item) => item[store.idKey] === this.query.getActiveId());
         if (activeItem) {
            store.updateActive(activeItem);
         }
         const entity = this.query.getActive();
         const result = [entity, ...items.filter((item) => item[store.idKey] !== this.query.getActiveId())].filter(
            (item): item is getEntityType<T> => item != undefined
         );
         store.set(result);
         return result;
      } else {
         store.set(items);
         return items;
      }
   }

   async refreshEntities(
      // workaround for child classes which have different parameters
      _organisationId?: Organisation['id'],
      _from?: ISO8601DateOnlyString,
      _to?: ISO8601DateOnlyString,
      _includeSubsidiaries?: boolean
   ) {
      return this.getEntities({ force: true });
   }

   async sortEntities(sortedEntityIds: getIDType<T>[], parentEntityName: string, entityId: getIDType<T>, collectionName: string) {
      if (sortedEntityIds.length === 0) {
         return Promise.resolve();
      }

      const path =
         parentEntityName && entityId && collectionName
            ? `/${this.version}/${parentEntityName}/${entityId}/${collectionName}/sort`
            : `/${this.version}/${this.entityName}/sort`;

      return this.httpClient
         .post<{ id: getIDType<T>; position: number }[]>(path, sortedEntityIds)
         .then((resp) =>
            applyTransaction(() =>
               resp.data.forEach(({ id, position }) => this.store.update(id, { position } as unknown as Partial<getEntityType<T>>))
            )
         )
         .catch((error) => {
            this.setError(error);
         });
   }

   async loadEntity(entityUniqueIdentifier: getIDType<T>, _options?: { force?: boolean }) {
      if (!this.queryParams) {
         await firstValueFrom(this.queryParamsObservable);
      }

      return this.httpClient
         .get<getEntityType<T>>(`/${this.version}/${this.entityName}/${entityUniqueIdentifier}${this.queryParams ?? ''}`)
         .then((resp) => {
            this.store.upsert(resp?.data?.id ?? entityUniqueIdentifier, resp.data);
            return resp?.data?.id;
         })
         .catch((error) => {
            this.setError(error);

            return false;
         });
   }

   getEntityState(entityId: getIDType<T>) {
      return this.query.getEntity(entityId);
   }

   hasEntity(entityId: getIDType<T>) {
      return this.query.hasEntity(entityId);
   }

   hasEntityByKey<K extends keyof T>(key: K, value: T[K]) {
      return this.query.hasEntity((entity) => entity[key] === value);
   }

   async updateActiveEntity(changes: Partial<T>) {
      const activeId = this.query.getActiveId();
      if (activeId !== undefined) {
         return this.updateEntity(activeId, changes);
      }
   }

   async updateEntity(entityId: getIDType<T>, changes: Partial<T>, updateStore = true, store = this.store, _options: any = {}) {
      return this.httpClient
         .patch<getEntityType<T>>(`/${this.version}/${this.entityName}/${entityId}`, changes)
         .then((resp) => {
            if (updateStore) {
               store.update(entityId, resp.data);
               if (this.paginate && this.paginator) {
                  this.paginator.refreshCurrentPage();
                  this.pageChanges$?.next(this.getPageChangesCombinedObservable());
               }
            }

            return resp.data;
         })

         .catch((error) => {
            this.setError(error);
            // opt-in: let callers handle the failure themselves
            if (_options?.throwOnError) {
               throw error;
            }
         });
   }

   createEntityState(entity: Partial<T>, setActive = false) {
      // eslint-disable-next-line @typescript-eslint/dot-notation
      const _ids = this.store['ids']; // private property workaround
      const id = Math.min(0, Math.min(..._ids) - 1);

      applyTransaction(() => {
         this.store.add({ ...entity, id } as getEntityType<T>);

         if (setActive) {
            this.store.setActive(id as T['active']);
         }
      });

      return Promise.resolve(id);
   }

   async createEntity(entity: Partial<T>, fetchAssociations = false, additionalQueryParams = '', addToStore = true, store = this.store) {
      let queryString = '';

      if (additionalQueryParams) {
         queryString = `?${additionalQueryParams.toString()}`;
      }

      return this.httpClient
         .post(`/${this.version}/${this.entityName}${queryString}`, isPlainObject(entity) ? omit(entity, 'id') : entity)
         .then(async (resp) => {
            const obj = resp.data;

            if (Array.isArray(this.collections)) {
               this.collections.forEach((associatedEntityName) => {
                  obj[associatedEntityName] ??= [];
               });
            } else if (isObject(this.collections)) {
               Object.entries(this.collections).forEach(([associatedEntityName]) => {
                  obj[associatedEntityName] ??= [];
               });
            }

            if (fetchAssociations) {
               await this.loadCollections(obj?.id);
            }

            if (Number.isInteger(entity?.id)) {
               store.remove(entity.id);
            }

            if (addToStore) {
               store.add(obj);

               if (this.paginate && this.paginator) {
                  this.paginator.refreshCurrentPage();
                  this.pageChanges$?.next(this.getPageChangesCombinedObservable());
               }
            }

            return obj;
         })
         .catch((error) => this.setError(error));
   }

   async deleteEntityFromCollection(entityId: getIDType<T>, collectionName: string, collectionEntityId: number) {
      return this.httpClient
         .delete(`/${this.version}/${this.entityName}/${entityId}/${collectionName.toLowerCase()}/${collectionEntityId}${this.queryParams ?? ''}`)
         .then(() =>
            this.store.update(
               entityId,
               ({ [collectionName]: collectionName_ }) =>
                  ({
                     [collectionName]: arrayRemove(collectionName_, collectionEntityId),
                  }) as Partial<getEntityType<T>>
            )
         )
         .catch((error) => {
            this.setError(error);
         });
   }

   async updateEntityCollection(entityId: getIDType<T>, collectionName: string, collectionEntityId: number | string, changes: Partial<T>) {
      return this.httpClient
         .patch(
            `/${this.version}/${this.entityName}/${entityId}/${collectionName.toLowerCase()}/${collectionEntityId}${this.queryParams ?? ''}`,
            omit(changes, ['fields', 'ui', 'updatedAt', 'createdAt', 'result', 'id'])
         )
         .then((resp) =>
            this.store.update(
               entityId,
               ({ [collectionName]: collectionName_ }) =>
                  ({
                     [collectionName]:
                        collectionName.toLowerCase() === 'notificationsettings'
                           ? resp.data
                           : arrayUpdate(collectionName_, collectionEntityId, resp.data),
                  }) as Partial<getEntityType<T>>
            )
         )
         .catch((error) => {
            this.setError(error);
         });
   }

   async deleteEntityFromActiveEntityCollection(collectionName: string, collectionEntityId: number) {
      const activeId = this.query.getActiveId();
      if (activeId !== undefined) {
         return this.deleteEntityFromCollection(activeId, collectionName, collectionEntityId);
      }
   }

   deleteCollectionEntityFromState(entityId: getIDType<T>, collectionName: string, collectionEntityId: number) {
      this.store.update(
         entityId,
         ({ [collectionName]: collectionName_ }) =>
            ({
               [collectionName]: arrayRemove(collectionName_, collectionEntityId),
            }) as Partial<getEntityType<T>>
      );
   }

   updateEntityCollectionState(entityId: getIDType<T>, collectionName: string, collectionEntityId: number, changes: Partial<T>) {
      this.store.update(
         entityId,
         ({ [collectionName]: collectionName_ }) =>
            ({
               [collectionName]: arrayUpsert(collectionName_, collectionEntityId, changes),
            }) as Partial<getEntityType<T>>
      );
   }

   replaceEntityCollectionState(entityId: getIDType<T>, collectionName: string, collectionState: T[]) {
      this.store.update(
         entityId,
         () =>
            ({
               [collectionName]: collectionState,
            }) as Partial<getEntityType<T>>
      );
   }

   setEntityCollectionState(entityId: getIDType<T>, collectionName: string, collectionState: T[]) {
      applyTransaction(() =>
         collectionState.forEach((collectionEntity) =>
            this.store.update(
               entityId,
               ({ [collectionName]: collectionName_ }) =>
                  ({
                     [collectionName]: arrayUpsert(collectionName_, collectionEntity.id, collectionEntity),
                  }) as Partial<getEntityType<T>>
            )
         )
      );
   }

   createEntityCollectionState(entityId: getIDType<T>, collectionName: string, collectionEntity: T) {
      let entity = this.query.getEntity(entityId);

      if (entity && !Array.isArray(entity?.[collectionName])) {
         this.store.update(entityId, { [collectionName]: [] } as Partial<getEntityType<T>>);
         entity = this.query.getEntity(entityId);
      }

      const collection = entity?.[collectionName];
      const id = collection?.length > 0 ? Math.min(0, Math.min(...collection.map((colEntity: any) => colEntity.id)) - 1) : 0;

      this.store.update(
         entityId,
         ({ [collectionName]: collectionName_ }) =>
            ({
               [collectionName]: arrayAdd(collectionName_, { ...collectionEntity, id }),
            }) as Partial<getEntityType<T>>
      );
   }

   addEntityCollectionState(entityId: getIDType<T>, collectionName: string, additionalItems: T[]) {
      const entity = this.query.getEntity(entityId);

      if (entity && !Array.isArray(entity?.[collectionName])) {
         this.store.update(entityId, { [collectionName]: [additionalItems].flat() } as Partial<getEntityType<T>>);
      }

      this.store.update(
         entityId,
         ({ [collectionName]: collectionName_ }) =>
            ({
               [collectionName]: arrayAdd(collectionName_, [additionalItems].flat()),
            }) as Partial<getEntityType<T>>
      );
   }

   async deleteEntity(entityIdOrArray: getIDType<T> | getIDType<T>[], _?: any, removeFromStore = true) {
      let entities;

      if (Array.isArray(entityIdOrArray)) {
         entities = entityIdOrArray;
      } else {
         entities = [entityIdOrArray];
      }

      const promises: Promise<any>[] = [];

      applyTransaction(() => {
         entities.forEach((entityId) => {
            const isActiveDeleted = this.query.hasActive() && this.query.getActiveId() === entityId;

            if (entityId <= 0) {
               this.store.remove(entityId);

               if (isActiveDeleted) {
                  this.store.setActive(null as T['active']);
               }
            } else {
               promises.push(
                  this.httpClient
                     .delete(`/${this.version}/${this.entityName}/${entityId}`)
                     .then(() =>
                        this.paginate ? this.store.setActive(null as T['active']) : removeFromStore ? this.store.remove(entityId) : undefined
                     )
                     .catch((error) => {
                        this.setError(error);
                     })
                     .finally(() => {
                        if (isActiveDeleted) {
                           this.store.setActive(null as T['active']);
                        }
                     })
               );
            }
         });
      });

      await Promise.all(promises);

      if (this.paginate && this.paginator) {
         this.paginator.refreshCurrentPage();
         this.pageChanges$?.next(this.getPageChangesCombinedObservable());
      }
   }

   deleteEntityFromState(entityIdOrArray: getIDType<T> | getIDType<T>[]) {
      let entities;

      if (Array.isArray(entityIdOrArray)) {
         entities = entityIdOrArray;
      } else {
         entities = [entityIdOrArray];
      }

      return this.store.remove(entities);
   }

   async deleteActiveEntity() {
      void this.deleteEntity(this.query.getActiveId() as getIDType<T>);
   }

   async setActiveEntity(
      entityUniqueIdentifier: getIDType<T>,
      loadCollections: boolean | string[] = true,
      refresh = false,
      loadCollectionParams?: string,
      options: any = {},
      store = this.store
   ) {
      if (this.query.hasActive() && this.query.getActiveId() === entityUniqueIdentifier && !refresh && entityUniqueIdentifier !== this.failedLoadId) {
         return undefined;
      }
      if (entityUniqueIdentifier === null) {
         return Promise.resolve(store.setActive(entityUniqueIdentifier));
      }
      if ((this.isLoading && !this.isInitial) || this.getError()) {
         return new Error(`Could not load entity ${entityUniqueIdentifier} while another load is running or has failed`);
      }

      this.isLoading = true;
      this.isInitial = false;

      const keyFields = ['id', 'key', 'slug', 'userName'];

      let entityId;
      let freshlyLoaded = false;

      if (this.query.hasEntity(entityUniqueIdentifier)) {
         const entityIdentifierAsInteger = Number.parseInt(entityUniqueIdentifier, 10);
         if (Number.isNaN(entityIdentifierAsInteger)) {
            entityId = entityUniqueIdentifier;
         } else {
            entityId = entityIdentifierAsInteger;
         }
      } else {
         const entity = this.query.getAll().find((entity_) => keyFields.some((field) => entity_?.[field] === entityUniqueIdentifier));

         if (entity) {
            entityId = entity[store.idKey];
         } else {
            store.setLoading(true);

            entityId = await this.loadEntity(entityUniqueIdentifier, options);
            freshlyLoaded = true;
         }
      }

      if (!entityId) {
         this.isLoading = false;
         store.setLoading(false);
         return Promise.resolve(new Error(`Could not find an entity with identifier ${entityUniqueIdentifier}`));
      }

      const reload = refresh || entityId === this.failedLoadId;

      if (this.query.hasActive(entityId) && !reload && loadCollections === false) {
         this.isLoading = false;
         return Promise.resolve(this.getLoading() ? store.setLoading(false) : undefined);
      }

      let loadFailed = false;

      if (this.query.hasEntity(entityId) && reload && !freshlyLoaded) {
         store.setLoading(true);
         loadFailed = (await this.loadEntity(entityId, options)) === false;
      }

      const collectionNames: string[] = [];

      if (loadCollections === true) {
         if (Array.isArray(this.collections)) {
            collectionNames.push(...this.collections);
         } else if (isObject(this.collections)) {
            collectionNames.push(...Object.keys(this.collections));
         }
      } else if (Array.isArray(loadCollections)) {
         collectionNames.push(...loadCollections);
      }

      if ((!this.query.hasActive(entityId) || (reload && !freshlyLoaded)) && loadCollections && collectionNames.length > 0) {
         store.setLoading(true);
         const loadedCollections = await this.loadCollections(entityId, collectionNames, loadCollectionParams);
         loadFailed ||= Array.isArray(loadedCollections) && loadedCollections.includes(false);
      }

      store.setActive(entityId);

      this.isLoading = false;
      this.failedLoadId = loadFailed ? entityId : undefined;

      if (this.getLoading()) {
         store.setLoading(false);
      }

      return loadFailed ? new Error(`Could not load entity ${entityId}`) : undefined;
   }

   setNextEntityActive(wrap = false) {
      return this.store.setActive({ next: true, wrap } as T['active']);
   }
   setPreviousEntityActive(wrap = false) {
      return this.store.setActive({ prev: true, wrap } as T['active']);
   }

   toggleUIState(entityId: getIDType<T>, property: string) {
      if (!this.store.ui) {
         // eslint-disable-next-line @typescript-eslint/no-base-to-string, @typescript-eslint/restrict-template-expressions
         throw new Error(`Entity UI Store is not defined for store ${this.store}`);
      }
      this.store.ui.update(entityId, (state: any) => ({
         [property]: !state[property],
      }));
   }

   updateUIState(entityId: getIDType<T>, property: string, value: any) {
      if (!this.store.ui) {
         // eslint-disable-next-line @typescript-eslint/no-base-to-string, @typescript-eslint/restrict-template-expressions
         throw new Error(`Entity UI Store is not defined for store ${this.store}`);
      }
      this.store.ui.update(entityId, { [property]: value });
   }

   updateActiveEntityState(changes: Partial<T>) {
      return this.store.updateActive(changes as Partial<getEntityType<T>>);
   }

   updateEntityState(entityId: getIDType<T>, changes: Partial<T>) {
      this.store.update(entityId, changes as Partial<getEntityType<T>>);
   }

   upsertEntityState(entityId: getIDType<T>, changes: Partial<T>) {
      this.store.upsert(entityId, changes as Partial<getEntityType<T>>);
   }

   async persistActiveEntityState(relevantKeys: string[] = [], queryString?: string) {
      const entity = this.query.getActive();

      return Promise.resolve(this.persistEntityState(entity as Partial<T>, relevantKeys, queryString));
   }

   async persistEntityState(entity_: Partial<T>, relevantKeys: string[] = [], queryString?: string) {
      let entity;

      entity = omit(entity_, ['createdAt', 'updatedAt', 'kpiFieldType']);

      if (relevantKeys.length > 0) {
         entity = pick(entity, relevantKeys);
      }

      if (!entity?.id || entity?.id <= 0) {
         return Promise.reject(new Error('[persistEntityState]: Entity has no id'));
      }

      return Promise.resolve(
         this.httpClient
            .patch(`/${this.version}/${this.entityName}/${entity.id}${queryString ?? ''}`, entity)
            .then((resp) => this.store.update(entity.id as getIDType<T>, resp.data))
            .catch((error) => {
               this.setError(error);
               throw error;
            })
      );
   }

   revertActiveEntityState() {
      const activeId = this.query.getActiveId();
      if (activeId && activeId <= 0) {
         this.store.remove(activeId);
      }
   }

   async loadCollection(
      entityId: getIDType<T>,
      collectionName: string,
      collectionStoreName?: string,
      additionalQueryParams?: string,
      setLoading = true,
      reset = false
   ) {
      if (setLoading) {
         this.store.setLoading(true);
      }

      if (!this.queryParams) {
         await firstValueFrom(this.queryParamsObservable);
      }

      const queryParams = new URLSearchParams(this.queryParams);

      if (additionalQueryParams) {
         const paramsToAdd = new URLSearchParams(additionalQueryParams);

         for (const [key, value] of paramsToAdd.entries()) {
            queryParams.set(key, value);
         }
      }

      return this.httpClient
         .get(`/${this.version}/${this.entityName}/${entityId}/${collectionName.toLowerCase()}?${queryParams.toString()}`)
         .then((resp) => {
            if (collectionStoreName && collectionStoreName !== this.store.storeName) {
               const selectedStore = getEntityStoreByName(collectionStoreName);

               if (!selectedStore) {
                  throw new Error(`Could not find store ${collectionStoreName}`);
               }

               if (reset) {
                  selectedStore.set(resp.data);
               } else {
                  selectedStore.upsertMany(resp.data, { loading: false });
               }
               this.store.setLoading(false);
               return true; // success
            }

            return applyTransaction(() => {
               this.store.update(entityId, { [collectionName]: resp.data } as Partial<getEntityType<T>>);
               this.store.setLoading(false);
               return true; // success
            });
         })
         .catch((error) => {
            this.setError(error);
            return false; // failure
         });
   }

   async loadCollections(entityId: getIDType<T>, collectionNames: string[] = [], queryParams?: string) {
      const promises: Promise<boolean>[] | number = [];

      if (
         !entityId ||
         entityId <= 0 ||
         !this.collections ||
         (Array.isArray(this.collections) && this.collections.length === 0) ||
         (isObject(this.collections) && Object.keys(this.collections).length === 0)
      ) {
         return Promise.resolve(false);
      }

      applyTransaction(() => {
         if (Array.isArray(this.collections)) {
            this.collections
               .filter((collectionName) => collectionNames.length === 0 || collectionNames.includes(collectionName))
               .forEach((collectionName) => {
                  promises.push(this.loadCollection(entityId, collectionName, this.store.storeName, queryParams, false));
               });
         } else if (isObject(this.collections)) {
            Object.entries(this.collections)
               .filter(([collectionName]) => collectionNames.length === 0 || collectionNames.includes(collectionName))
               .forEach(([collectionName, collectionStoreName]) =>
                  promises.push(this.loadCollection(entityId, collectionName, collectionStoreName, queryParams, false))
               );
         }
      });
      return Promise.all(promises);
   }

   hasActiveEntity(entityId?: getIDType<T>) {
      return this.query.hasActive(entityId);
   }

   addActiveEntity(entityId: getIDType<T>) {
      return this.store.addActive(entityId);
   }

   removeActiveEntity(entityId: getIDType<T>) {
      return this.store.removeActive(entityId);
   }

   toggleActiveEntity(entityId: getIDType<T>) {
      return this.store.toggleActive(entityId);
   }

   // TODO: inform Thimo about change from array of ids to single id
   setActiveEntities(entityId: getIDType<T>) {
      if (!entityId) return;
      return this.store.setActive(entityId);
   }

   async updateEntityFromCollection(entityId: getIDType<T>, collectionName: string, collectionEntityId: number, changes: Partial<T>) {
      return this.httpClient
         .patch(`/${this.version}/${this.entityName}/${collectionEntityId}/${collectionName.toLowerCase()}`, changes)
         .then((resp) =>
            this.store.update(
               entityId,
               ({ [collectionName]: collectionName_ }) =>
                  ({
                     [collectionName]: arrayUpdate(collectionName_, collectionEntityId, resp.data),
                  }) as Partial<getEntityType<T>>
            )
         )
         .catch((error) => {
            this.setError(error);
         });
   }

   async addEntityToCollection(entityId: getIDType<T>, collectionName: string, changes: Partial<T>) {
      return this.httpClient
         .post(`/${this.version}/${this.entityName}/${entityId}/${collectionName.toLowerCase()}${this.queryParams ?? ''}`, changes)
         .then((resp) => {
            this.store.update(
               entityId,
               ({ [collectionName]: collectionName_ }) =>
                  ({
                     [collectionName]: arrayAdd(collectionName_, resp.data),
                  }) as Partial<getEntityType<T>>
            );
            return resp.data;
         })
         .catch((error) => {
            this.setError(error);
         });
   }

   async addEntitiesToCollection(entityId: getIDType<T>, collectionName: string, changes: Partial<any> | Partial<any>[]) {
      return this.httpClient
         .patch(`/${this.version}/${this.entityName}/${entityId}/${collectionName.toLowerCase()}${this.queryParams ?? ''}`, changes)
         .then((resp) => this.store.update(entityId, { [collectionName]: resp.data } as Partial<getEntityType<T>>))
         .catch((error) => {
            this.setError(error);
         });
   }

   async setEntitiesInCollection(entityId: getIDType<T>, collectionName: string, entities: getIDType<T>[]) {
      return this.httpClient
         .put(`/${this.version}/${this.entityName}/${entityId}/${collectionName.toLowerCase()}`, entities)
         .then((resp) => this.store.update(entityId, { [collectionName]: resp.data } as Partial<getEntityType<T>>))
         .catch((error) => {
            this.setError(error);
         });
   }

   setRequestLocale(locale: string) {
      this.httpClient.defaults.headers.common['Accept-Language'] = locale;
   }

   updateStoreState<K extends keyof T>(key: K, value: T[K]) {
      this.store.update({ [key]: value } as unknown as Partial<T>);
   }

   async getHistory(entityId: getIDType<T>, includeChildObjects = true, level = 'LOG', additionalId: getIDType<T> | null = null) {
      const collectionName = 'history';

      // TODO pagination, lazy loading

      let paramsString;

      if (this.queryParams) {
         paramsString = this.queryParams;
      } else {
         paramsString = await firstValueFrom(this.queryParamsObservable);
      }

      const params = new URLSearchParams(paramsString);

      params.set('includeChildObjects', String(includeChildObjects));
      params.set('level', level);

      if (params.has('from')) {
         params.set('validFrom', params.get('from') ?? '');
         params.delete('from');
      }

      if (params.has('to')) {
         params.set('validTo', params.get('to') ?? '');
         params.delete('to');
      }

      return this.httpClient
         .get(`/${this.version}/${this.entityName}/${entityId ? entityId.toString().concat('/') : ''}${collectionName.toLowerCase()}?${params ?? ''}`)
         .then(({ data }) => {
            // TODO: move this to respective component services
            if (['kpiFields', 'taxonomy/businessactivities'].includes(this.store.storeName)) {
               this.store.update(entityId, {
                  history: data,
               } as unknown as Partial<getEntityType<T>>);
            } else {
               this.store.update(
                  additionalId,
                  ({ calculations }) =>
                     ({
                        calculations: (calculations as any[]).map((calculation) => ({
                           ...calculation,
                           history: calculation?.id === entityId ? data : null,
                        })),
                     }) as unknown as Partial<getEntityType<T>>
               );
            }
            const storeName = this?.store?.storeName ?? '';
            return this.historyService.setHistoryReference(storeName, entityId, additionalId);
         })
         .catch((error) => {
            this.setError(error);
         });
   }

   async persistEntity(entityOrId: T | getIDType<T>) {
      let entity: T | undefined;

      if (isPlainObject(entityOrId)) {
         entity = entityOrId;
      } else if (typeof entityOrId === 'string') {
         entity = this.query.getEntity(entityOrId);
      }

      if (!entity?.id || entity?.id <= 0) {
         return this.createEntity(entity as Partial<T>);
      }

      return applyTransaction(() =>
         this.httpClient
            .patch(`/${this.version}/${this.entityName}/${entity.id}`, entity)
            .then(({ data }) => this.store.update(entity.id, data))
            .catch((error) => {
               this.setError(error);
            })
      );
   }

   unsubscribe() {
      this._setParams.unsubscribe();
      this.subscriptions.unsubscribeAll();
      this.requests.abortAll('[Service.unsubscribe] requests aborted');
   }

   async setAllowedAuditors(entityId: getIDType<T>, changes: Partial<T>) {
      const collectionName = 'allowedauditors';

      return this.httpClient
         .post(`/${this.version}/${this.entityName}/${entityId}/${collectionName.toLowerCase()}${this.queryParams ?? ''}`, changes)
         .then((resp) =>
            this.store.update(entityId, {
               allowedAuditors: resp.data?.allowedAuditors ?? [],
               allowedAuditorGroups: resp.data?.allowedAuditorGroups ?? [],
            } as unknown as Partial<getEntityType<T>>)
         )
         .catch((error) => {
            this.setError(error);
         });
   }

   async setAllowedEditors(entityId: getIDType<T>, changes: { userIds: number[]; groupIds: number[] }) {
      const collectionName = 'allowededitors';

      return this.httpClient
         .post<{ allowedEditors: any[]; allowedEditorGroups: any[] }>(
            `/${this.version}/${this.entityName}/${entityId}/${collectionName.toLowerCase()}${this.queryParams ?? ''}`,
            changes
         )
         .then(({ data }) =>
            this.store.update(entityId, {
               allowedEditors: data.allowedEditors ?? [],
               allowedEditorGroups: data?.allowedEditorGroups ?? [],
            } as unknown as Partial<getEntityType<T>>)
         )
         .catch((error) => {
            this.setError(error);
         });
   }

   // A request without a response, or a 500, is reported here and marked as reported. The caller reports any other failure.
   protected auditRequestFailed(error: AxiosError): FailedAuditRequest {
      if (!error.response || error.response.status === 500) {
         this.setError(error, this.auditErrorStore);

         return markAuditFailureReported(error.response ?? error);
      }

      return error.response;
   }

   #setStatus(
      status: StatusVerificationEnumExtended,
      entityId: getIDType<T>,
      organisationId: Organisation['organisationId'],
      from: Date,
      to: Date,
      entityName: string,
      auditValues: getIDType<T>[] = []
   ) {
      const user = pick(this.userContextQuery.getValue(), [
         'id',
         'salutation',
         'image',
         'userName',
         'firstName',
         'lastName',
         'email',
         'lang',
         'locale',
      ]) as User;

      const setStatusValues: {
         status: StatusVerificationTypeEnum;
         reviewerId?: number | null;
         approverId?: number | null;
         reviewer?: typeof user | null;
         approver?: typeof user | null;
      } = {
         status: ApprovalStatus.IN_PROGRESS,
      };

      switch (status) {
         case ApprovalStatus.IN_PROGRESS:
            setStatusValues.reviewer = null;
            setStatusValues.approver = null;
            setStatusValues.reviewerId = null;
            setStatusValues.approverId = null;
            setStatusValues.status = status;
            break;
         case ApprovalStatus.REVIEWED:
            setStatusValues.reviewer = user;
            setStatusValues.reviewerId = user.id;
            setStatusValues.approver = null;
            setStatusValues.approverId = null;
            setStatusValues.status = status;
            break;
         case ApprovalStatus.APPROVED:
            setStatusValues.approver = user;
            setStatusValues.approverId = user.id;
            setStatusValues.status = status;
            break;
         case ApprovalStatus.ARCHIVED:
            setStatusValues.status = status;
            break;
         case 'UNARCHIVE':
            setStatusValues.status = ApprovalStatus.APPROVED;
            break;
         default:
            break;
      }

      if (entityName === 'kpifields' && typeof this.setStatusApprovalCenter === 'function') {
         const collectionName = 'rows';

         this.setStatusApprovalCenter(setStatusValues, auditValues);
         this.store.update((entity) => auditValues.includes(entity.id), { ...setStatusValues } as unknown as Partial<getEntityType<T>>);
         this.store.update(
            (entity) => auditValues.includes(entity.id),
            ({ [collectionName]: collectionName_, attachments }) =>
               ({
                  [collectionName]: collectionName_.map((row: any) => ({
                     ...row,
                     ...setStatusValues,
                     values: (row?.values ?? []).map((value: any) => ({
                        ...value,
                        ...setStatusValues,
                     })),
                     formulas: (row?.formulas ?? []).map((value: any) => ({
                        ...value,
                        ...setStatusValues,
                     })),
                  })),
                  attachments: attachments.map((attachment: Attachment) => ({
                     ...attachment,
                     ...setStatusValues,
                  })),
               }) as unknown as Partial<getEntityType<T>>
         );
         this?.kpiValuesStore?.update(
            (entity: any) =>
               auditValues.includes(entity.kpiFieldId) &&
               entity.organisationId === organisationId &&
               parseISO(entity.from) >= from &&
               (parseISO(entity.to) <= to || !entity.to),
            { ...setStatusValues }
         );
      } else if (entityName === 'kpisubs' && typeof this.setStatusApprovalCenter === 'function') {
         this.setStatusApprovalCenter(setStatusValues, auditValues);
         this?.kpiFieldsStore?.update((entity: any) => entity.kpiContent?.kpiSub?.id === entityId && auditValues.includes(entity.id), {
            ...setStatusValues,
         });
      } else if (entityName === 'taxonomy/businessactivities' && typeof this.setStatusApprovalCenter === 'function') {
         this.setStatusApprovalCenter(setStatusValues, [entityId]);
         this.store.update(
            entityId,
            ({ taxonomyActivityAssignments, organisationAssignments }) =>
               ({
                  taxonomyActivityAssignments: Array.isArray(taxonomyActivityAssignments)
                     ? arrayUpdate(
                          taxonomyActivityAssignments,
                          (assignment: TaxonomyActivityAssignment) =>
                             parseISO(assignment.validFrom) >= from &&
                             parseISO(assignment.validTo) <= to &&
                             assignment.organisation.id === organisationId,
                          { ...setStatusValues }
                       )
                     : [],
                  organisationAssignments: Array.isArray(organisationAssignments)
                     ? arrayUpdate(
                          organisationAssignments,
                          (assignment: OrganisationAssignment) =>
                             parseISO(assignment.validFrom) >= from &&
                             parseISO(assignment.validTo) <= to &&
                             assignment.organisation.id === organisationId,
                          { ...setStatusValues }
                       )
                     : [],
               }) as unknown as Partial<getEntityType<T>>
         );
      } else if (entityName === 'taxonomy/minimumsafeguards' && typeof this.setStatus === 'function') {
         this.setStatus(organisationId, from, to, setStatusValues, auditValues);
      } else if (entityName === 'taxonomy/fslis' && typeof this.setStatus === 'function') {
         this.setStatus(organisationId, from, to, setStatusValues, auditValues);
      } else if (entityName === 'taxonomy/climaterisk/analyses' && typeof this.setStatusClimateRisk === 'function') {
         this.setStatusClimateRisk(setStatusValues);
      } else if (entityName === 'kpivalues' && typeof this.setStatusApprovalCenter === 'function') {
         const auditValue = auditValues[0];
         this.setStatusApprovalCenter(setStatusValues, [auditValue.id], 'kpivalue');
         this?.kpiFieldsStore?.update((entity: any) => auditValue?.kpiFieldId === entity.id, {
            ...setStatusValues,
         });
         this.store.update((entity) => auditValue?.id === entity.id, { ...setStatusValues } as unknown as Partial<getEntityType<T>>);
      } else if (entityName === 'kpivalueitem' && typeof this.setStatusApprovalCenter === 'function') {
         this.setStatusApprovalCenter(setStatusValues, [entityId], 'kpivalueitem');
      } else if (entityName === 'currenciesrates' && typeof this.setStatusApprovalCenter === 'function') {
         this.setStatusApprovalCenter(setStatusValues, auditValues, 'currencyrate');
         this.store.update((entity) => auditValues.includes(entity.id), { ...setStatusValues } as unknown as Partial<getEntityType<T>>);
      } else if (['emissions/ghg/footprintssubs', 'emissions/ghg/footprints'].includes(entityName)) {
         const activeFootprintId = this.query.getActiveId();
         const collectionName = 'subs';

         if (activeFootprintId) {
            if (entityName === 'emissions/ghg/footprintssubs') {
               this.store.update(
                  activeFootprintId,
                  ({ [collectionName]: collectionName_ }) =>
                     ({
                        [collectionName]: arrayUpdate(collectionName_, (item) => item?.id === entityId, { ...setStatusValues } as unknown as Partial<
                           getEntityType<T>
                        >),
                     }) as unknown as Partial<getEntityType<T>>
               );
            } else if (entityName === 'emissions/ghg/footprints') {
               this.store.update(activeFootprintId, (footprint) => ({
                  ...footprint,
                  ...setStatusValues,
                  subs: footprint.subs.map((sub: FootprintSub) => ({
                     ...sub,
                     ...setStatusValues,
                  })),
               }));
            }
            if (typeof this.setStatusApprovalCenter === 'function') {
               this.setStatusApprovalCenter(setStatusValues, auditValues);
            }
         }
      }
   }

   async reviewEntity(entityIdOrStandard: string | getIDType<T>, forceAudit = false, modelName = '', justification = '') {
      const userContext = this.userContextQuery.getValue();
      const reviewerId = userContext?.id;
      const entityName = this.entityName;
      const includeSubsidiaries = userContext?.includeSubsidiaries;
      let organisationId: Organisation['id'] | undefined;

      if (this.useOrganisationContext || entityName === 'kpiscopes' || entityName === 'scoping/esrs/materialityassessments') {
         organisationId = userContext?.activeOrganisation?.id;
      }

      let from: Date;
      let to: Date;
      if (userContext?.activePeriod) {
         from = parseISO(userContext?.activePeriod?.from);
         to = parseISO(userContext?.activePeriod?.to);
      }

      if (!this.queryParams) {
         await firstValueFrom(this.queryParamsObservable);
      }

      const { path, queryParams } = determineAuditActionEntityPathSegmentAndParameters({
         entityName,
         entityIdOrStandard,
         modelName,
         includeSubsidiaries,
         params: this.queryParams,
         forceAudit,
      });

      const setStatusToReviewed = (
         auditValues: any[],
         _entityName = entityName,
         approvalStatus: StatusVerificationTypeEnum = ApprovalStatus.REVIEWED
      ) => {
         return this.#setStatus(approvalStatus, entityIdOrStandard as getIDType<T>, organisationId, from, to, _entityName, auditValues);
      };

      return this.httpClient
         .post(`/${this.version}/${path}/review?${queryParams.toString() ?? ''}`, justification ? { justification } : undefined)
         .then((resp) => {
            if (!forceAudit && resp.data?.recalculatedResult !== undefined) {
               return resp.data;
            }
            if (entityName === 'kpifields') {
               void this.refreshCurrentKPIProgress?.();
               return setStatusToReviewed(resp.data.auditValues);
            } else if (
               ['kpisubs', 'kpivalues', 'currenciesrates'].includes(entityName) ||
               ([
                  'taxonomy/businessactivities',
                  'taxonomy/minimumsafeguards',
                  'taxonomy/fslis',
                  'taxonomy/climaterisk/analyses',
                  'emissions/ghg/footprintssubs',
                  'emissions/ghg/footprints',
               ].includes(entityName) &&
                  !modelName)
            ) {
               return setStatusToReviewed(resp.data.auditValues);
            } else if (['kpiValueItem', 'kpivalueitem'].includes(modelName ?? '')) {
               setStatusToReviewed(resp.data.auditValues, modelName);
               return [];
            } else if (entityName === 'kpiscopes' && organisationId) {
               return this?.kpiScopesStore?.update(getScopeFilterFunction(from, to, organisationId), {
                  status: ApprovalStatus.REVIEWED,
                  reviewerId,
               });
            } else if (entityName === 'scoping/esrs/materialityassessments' && organisationId) {
               this?.kpiScopesStore?.update(getScopeFilterFunction(from, to, organisationId), {
                  status: ApprovalStatus.REVIEWED,
                  reviewerId,
               });
            } else if (modelName === 'attachment') {
               return;
            }

            const user = pick(this.userContextQuery.getValue(), [
               'id',
               'salutation',
               'image',
               'userName',
               'firstName',
               'lastName',
               'email',
               'lang',
               'locale',
            ]);

            return this.store.update(entityIdOrStandard as getIDType<T>, {
               status: ApprovalStatus.REVIEWED,
               reviewerId,
               reviewer: user,
               ...resp.data,
            });
         })
         .catch((error) => this.auditRequestFailed(error));
   }

   async unreviewEntity(entityIdOrStandard: string | getIDType<T>, forceAudit = false, modelName = '') {
      const userContext = this.userContextQuery.getValue();
      let organisationId: Organisation['id'] | undefined;
      const entityName = this.entityName;
      const includeSubsidiaries = userContext?.includeSubsidiaries;

      if (this.useOrganisationContext || entityName === 'kpiscopes' || entityName === 'scoping/esrs/materialityassessments') {
         organisationId = userContext?.activeOrganisation?.id;
      }

      let from: Date;
      let to: Date;
      if (userContext?.activePeriod) {
         from = parseISO(userContext?.activePeriod?.from);
         to = parseISO(userContext?.activePeriod?.to);
      }

      if (!this.queryParams) {
         await firstValueFrom(this.queryParamsObservable);
      }

      const { path, queryParams } = determineAuditActionEntityPathSegmentAndParameters({
         entityName,
         entityIdOrStandard,
         modelName,
         includeSubsidiaries,
         params: this.queryParams,
         forceAudit,
      });

      const setStatusToUnreview = (
         auditValues: any[],
         _entityName = entityName,
         approvalStatus: StatusVerificationTypeEnum = ApprovalStatus.IN_PROGRESS
      ) => {
         return this.#setStatus(approvalStatus, entityIdOrStandard as getIDType<T>, organisationId, from, to, _entityName, auditValues);
      };

      return this.httpClient
         .post(`/${this.version}/${path}/unreview?${queryParams.toString() ?? ''}`)
         .then((resp) => {
            if (entityName === 'kpifields') {
               void this.refreshCurrentKPIProgress?.();
               return setStatusToUnreview(resp.data.auditValues);
            } else if (
               ['kpisubs', 'kpivalues', 'currenciesrates'].includes(entityName) ||
               ([
                  'taxonomy/businessactivities',
                  'taxonomy/minimumsafeguards',
                  'taxonomy/fslis',
                  'taxonomy/climaterisk/analyses',
                  'emissions/ghg/footprintssubs',
                  'emissions/ghg/footprints',
               ].includes(entityName) &&
                  !modelName)
            ) {
               return setStatusToUnreview(resp.data.auditValues);
            } else if (['kpiValueItem', 'kpivalueitem'].includes(modelName ?? '')) {
               setStatusToUnreview(resp.data.auditValues, modelName);
               return [];
            } else if (entityName === 'kpiscopes' && organisationId) {
               return this?.kpiScopesStore?.update(getScopeFilterFunction(from, to, organisationId), {
                  status: ApprovalStatus.IN_PROGRESS,
                  reviewerId: null,
                  reviewer: null,
               });
            } else if (entityName === 'scoping/esrs/materialityassessments' && organisationId) {
               this?.kpiScopesStore?.update(getScopeFilterFunction(from, to, organisationId), {
                  status: ApprovalStatus.IN_PROGRESS,
                  reviewerId: null,
                  reviewer: null,
               });
            } else if (modelName === 'attachment') {
               return;
            }

            return this.store.update(entityIdOrStandard as getIDType<T>, {
               ...resp.data,
               status: ApprovalStatus.IN_PROGRESS,
               reviewerId: null,
               reviewer: null,
               approverId: null,
               approver: null,
            });
         })
         .catch((error) => this.auditRequestFailed(error));
   }

   async approveEntity(entityIdOrStandard: string | getIDType<T>, forceAudit = false, modelName = '') {
      const userContext = this.userContextQuery.getValue();
      const approverId = userContext?.id;
      const entityName = this.entityName;
      const includeSubsidiaries = userContext?.includeSubsidiaries;

      let organisationId: Organisation['id'] | undefined;
      if (this.useOrganisationContext || entityName === 'kpiscopes' || entityName === 'scoping/esrs/materialityassessments') {
         organisationId = userContext?.activeOrganisation?.id;
      }

      let from: Date;
      let to: Date;
      if (userContext?.activePeriod) {
         from = parseISO(userContext?.activePeriod?.from);
         to = parseISO(userContext?.activePeriod?.to);
      }

      if (!this.queryParams) {
         await firstValueFrom(this.queryParamsObservable);
      }

      const { path, queryParams } = determineAuditActionEntityPathSegmentAndParameters({
         entityName,
         entityIdOrStandard,
         modelName,
         includeSubsidiaries,
         params: this.queryParams,
         forceAudit,
      });

      const setStatusToApprove = (
         auditValues: any[],
         _entityName = entityName,
         approvalStatus: StatusVerificationTypeEnum = ApprovalStatus.APPROVED
      ) => {
         return this.#setStatus(approvalStatus, entityIdOrStandard as getIDType<T>, organisationId, from, to, _entityName, auditValues);
      };

      return this.httpClient
         .post(`/${this.version}/${path}/approve?${queryParams.toString() ?? ''}`)
         .then((resp) => {
            if (entityName === 'kpifields') {
               void this.refreshCurrentKPIProgress?.();
               return setStatusToApprove(resp.data.auditValues);
            } else if (
               ['kpisubs', 'kpivalues', 'currenciesrates'].includes(entityName) ||
               ([
                  'taxonomy/businessactivities',
                  'taxonomy/minimumsafeguards',
                  'taxonomy/fslis',
                  'taxonomy/climaterisk/analyses',
                  'emissions/ghg/footprintssubs',
                  'emissions/ghg/footprints',
               ].includes(entityName) &&
                  !modelName)
            ) {
               return setStatusToApprove(resp.data.auditValues);
            } else if (['kpiValueItem', 'kpivalueitem'].includes(modelName ?? '')) {
               setStatusToApprove(resp.data.auditValues, modelName);
               return [];
            } else if (entityName === 'kpiscopes' && organisationId) {
               return this?.kpiScopesStore?.update(getScopeFilterFunction(from, to, organisationId), {
                  status: ApprovalStatus.APPROVED,
                  approverId,
               });
            } else if (entityName === 'scoping/esrs/materialityassessments' && organisationId) {
               this?.kpiScopesStore?.update(getScopeFilterFunction(from, to, organisationId), {
                  status: ApprovalStatus.APPROVED,
                  approverId,
               });
            } else if (modelName === 'attachment') {
               return;
            }

            const user = pick(this.userContextQuery.getValue(), [
               'id',
               'salutation',
               'image',
               'userName',
               'firstName',
               'lastName',
               'email',
               'lang',
               'locale',
            ]);

            this.store.update(entityIdOrStandard as getIDType<T>, {
               status: ApprovalStatus.APPROVED,
               approverId,
               approver: user,
               ...resp.data,
            });

            if (entityName === 'scoping/esrs/materialityassessments') {
               return window.location.reload();
            }

            return true;
         })
         .catch((error) => this.auditRequestFailed(error));
   }

   async unapproveEntity(entityIdOrStandard: string | getIDType<T>, forceAudit = false, modelName = '') {
      const userContext = this.userContextQuery.getValue();
      let organisationId: Organisation['id'] | undefined;
      const entityName = this.entityName;
      const includeSubsidiaries = userContext?.includeSubsidiaries;

      if (this.useOrganisationContext || entityName === 'kpiscopes' || entityName === 'scoping/esrs/materialityassessments') {
         organisationId = userContext?.activeOrganisation?.id;
      }

      let from: Date;
      let to: Date;
      if (userContext?.activePeriod) {
         from = parseISO(userContext?.activePeriod?.from);
         to = parseISO(userContext?.activePeriod?.to);
      }

      if (!this.queryParams) {
         await firstValueFrom(this.queryParamsObservable);
      }

      const { path, queryParams } = determineAuditActionEntityPathSegmentAndParameters({
         entityName,
         entityIdOrStandard,
         modelName,
         includeSubsidiaries,
         params: this.queryParams,
         forceAudit,
      });

      const setStatusToUnapprove = (
         auditValues: any[],
         _entityName = entityName,
         approvalStatus: StatusVerificationTypeEnum = ApprovalStatus.IN_PROGRESS
      ) => {
         return this.#setStatus(approvalStatus, entityIdOrStandard as getIDType<T>, organisationId, from, to, _entityName, auditValues);
      };

      return this.httpClient
         .post(`/${this.version}/${path}/unapprove?${queryParams.toString() ?? ''}`)
         .then((resp) => {
            if (entityName === 'kpifields') {
               void this.refreshCurrentKPIProgress?.();
               return setStatusToUnapprove(resp.data.auditValues);
            } else if (
               ['kpisubs', 'kpivalues'].includes(entityName) ||
               ([
                  'taxonomy/businessactivities',
                  'taxonomy/minimumsafeguards',
                  'taxonomy/fslis',
                  'taxonomy/climaterisk/analyses',
                  'emissions/ghg/footprintssubs',
                  'emissions/ghg/footprints',
               ].includes(entityName) &&
                  !modelName)
            ) {
               return setStatusToUnapprove(resp.data.auditValues);
            } else if (entityName === 'currenciesrates' && typeof this.setStatusApprovalCenter === 'function') {
               const statusValues = { approver: null, approverId: null, status: ApprovalStatus.REVIEWED };
               this.setStatusApprovalCenter(statusValues, resp.data.auditValues, 'currencyrate');
               this.store.update((entity) => resp.data.auditValues.includes(entity.id), statusValues as unknown as Partial<getEntityType<T>>);
            } else if (['kpiValueItem', 'kpivalueitem'].includes(modelName ?? '')) {
               setStatusToUnapprove(resp.data.auditValues, modelName);
               return [];
            } else if (entityName === 'kpiscopes' && organisationId) {
               return this?.kpiScopesStore?.update(getScopeFilterFunction(from, to, organisationId), {
                  status: ApprovalStatus.IN_PROGRESS,
                  approverId: null,
                  approver: null,
                  reviewerId: null,
                  reviewer: null,
               });
            } else if (entityName === 'scoping/esrs/materialityassessments' && organisationId) {
               this?.kpiScopesStore?.update(getScopeFilterFunction(from, to, organisationId), {
                  status: ApprovalStatus.IN_PROGRESS,
                  approverId: null,
                  approver: null,
                  reviewerId: null,
                  reviewer: null,
               });
            } else if (modelName === 'attachment') {
               return;
            }

            return this.store.update(entityIdOrStandard as getIDType<T>, {
               status: ApprovalStatus.IN_PROGRESS,
               approverId: null,
               approver: null,
               reviewerId: null,
               reviewer: null,
               ...resp.data,
            });
         })
         .catch((error) => this.auditRequestFailed(error));
   }

   async rejectEntity(entityIdOrStandard: string | getIDType<T>, forceAudit = false, justification: string, modelName = '') {
      const userContext = this.userContextQuery.getValue();
      let organisationId: Organisation['id'] | undefined;
      const entityName = this.entityName;
      const includeSubsidiaries = userContext?.includeSubsidiaries;

      if (this.useOrganisationContext || entityName === 'kpiscopes' || entityName === 'scoping/esrs/materialityassessments') {
         organisationId = userContext?.activeOrganisation?.id;
      }

      let from: Date;
      let to: Date;
      if (userContext?.activePeriod) {
         from = parseISO(userContext?.activePeriod?.from);
         to = parseISO(userContext?.activePeriod?.to);
      }

      if (!this.queryParams) {
         await firstValueFrom(this.queryParamsObservable);
      }

      const { path, queryParams } = determineAuditActionEntityPathSegmentAndParameters({
         entityName,
         entityIdOrStandard,
         modelName,
         includeSubsidiaries,
         params: this.queryParams,
         forceAudit,
      });

      const setStatusToReject = (
         auditValues: any[],
         _entityName = entityName,
         approvalStatus: StatusVerificationTypeEnum = ApprovalStatus.IN_PROGRESS
      ) => {
         return this.#setStatus(approvalStatus, entityIdOrStandard as getIDType<T>, organisationId, from, to, _entityName, auditValues);
      };
      return this.httpClient
         .post(`/${this.version}/${path}/reject?${queryParams.toString() ?? ''}`, { justification })
         .then((resp) => {
            if (entityName === 'kpifields') {
               void this.refreshCurrentKPIProgress?.();
               return setStatusToReject(resp.data.auditValues);
            } else if (
               ['kpisubs', 'kpivalues', 'currenciesrates'].includes(entityName) ||
               ([
                  'taxonomy/businessactivities',
                  'taxonomy/minimumsafeguards',
                  'taxonomy/fslis',
                  'taxonomy/climaterisk/analyses',
                  'emissions/ghg/footprintssubs',
                  'emissions/ghg/footprints',
               ].includes(entityName) &&
                  !modelName)
            ) {
               return setStatusToReject(resp.data.auditValues);
            } else if (['kpiValueItem', 'kpivalueitem'].includes(modelName ?? '')) {
               setStatusToReject(resp.data.auditValues, modelName);
               return;
            } else if (entityName === 'kpiscopes' && organisationId) {
               return this?.kpiScopesStore?.update(getScopeFilterFunction(from, to, organisationId), {
                  status: ApprovalStatus.IN_PROGRESS,
                  approverId: null,
                  reviewerId: null,
               });
            } else if (entityName === 'scoping/esrs/materialityassessments' && organisationId) {
               this?.kpiScopesStore?.update(getScopeFilterFunction(from, to, organisationId), {
                  status: ApprovalStatus.IN_PROGRESS,
                  approverId: null,
                  reviewerId: null,
               });
            } else if (modelName === 'attachment') {
               return;
            }

            return this.store.update(entityIdOrStandard as getIDType<T>, {
               status: ApprovalStatus.IN_PROGRESS,
               approverId: null,
               approver: null,
               reviewerId: null,
               reviewer: null,
               ...resp.data,
            });
         })
         .catch((error: AxiosError) => this.auditRequestFailed(error));
   }

   async archiveEntity(entityIdOrStandard: string | getIDType<T>, forceAudit = false, modelName = '') {
      const userContext = this.userContextQuery.getValue();
      let organisationId: Organisation['id'] | undefined;
      const entityName = this.entityName;
      const includeSubsidiaries = userContext?.includeSubsidiaries;

      if (this.useOrganisationContext || entityName === 'kpiscopes' || entityName === 'scoping/esrs/materialityassessments') {
         organisationId = userContext?.activeOrganisation?.id;
      }

      let from: Date;
      let to: Date;
      if (userContext?.activePeriod) {
         from = parseISO(userContext?.activePeriod?.from);
         to = parseISO(userContext?.activePeriod?.to);
      }

      if (!this.queryParams) {
         await firstValueFrom(this.queryParamsObservable);
      }

      const { path, queryParams } = determineAuditActionEntityPathSegmentAndParameters({
         entityName,
         entityIdOrStandard,
         modelName,
         includeSubsidiaries,
         params: this.queryParams,
         forceAudit,
      });

      const setStatusToArchive = (
         auditValues: any[],
         _entityName = entityName,
         approvalStatus: StatusVerificationTypeEnum = ApprovalStatus.ARCHIVED
      ) => {
         return this.#setStatus(approvalStatus, entityIdOrStandard as getIDType<T>, organisationId, from, to, _entityName, auditValues);
      };

      return this.httpClient
         .post(`/${this.version}/${path}/archive?${queryParams.toString() ?? ''}`)
         .then((resp) => {
            if (entityName === 'kpifields') {
               void this.refreshCurrentKPIProgress?.();
               return setStatusToArchive(resp.data.auditValues);
            } else if (
               ['kpisubs', 'kpivalues', 'currenciesrates'].includes(entityName) ||
               ([
                  'taxonomy/businessactivities',
                  'taxonomy/minimumsafeguards',
                  'taxonomy/fslis',
                  'taxonomy/climaterisk/analyses',
                  'emissions/ghg/footprintssubs',
                  'emissions/ghg/footprints',
               ].includes(entityName) &&
                  !modelName)
            ) {
               return setStatusToArchive(resp.data.auditValues);
            } else if (['kpiValueItem', 'kpivalueitem'].includes(modelName ?? '')) {
               setStatusToArchive(resp.data.auditValues, modelName);
               return [];
            } else if (entityName === 'kpiscopes' && organisationId) {
               return this?.kpiScopesStore?.update(getScopeFilterFunction(from, to, organisationId), {
                  status: ApprovalStatus.ARCHIVED,
               });
            } else if (entityName === 'scoping/esrs/materialityassessments' && organisationId) {
               this?.kpiScopesStore?.update(getScopeFilterFunction(from, to, organisationId), {
                  status: ApprovalStatus.ARCHIVED,
               });
            } else if (modelName === 'attachment') {
               return;
            }

            return this.store.update(entityIdOrStandard as getIDType<T>, { status: ApprovalStatus.ARCHIVED, ...resp.data });
         })
         .catch((error) => this.auditRequestFailed(error));
   }

   async unarchiveEntity(entityIdOrStandard: string | getIDType<T>, forceAudit = false, modelName = '') {
      const userContext = this.userContextQuery.getValue();
      let organisationId: Organisation['id'] | undefined;
      const entityName = this.entityName;
      const includeSubsidiaries = userContext?.includeSubsidiaries;

      if (this.useOrganisationContext || entityName === 'kpiscopes' || entityName === 'scoping/esrs/materialityassessments') {
         organisationId = userContext?.activeOrganisation?.id;
      }

      let from: Date;
      let to: Date;
      if (userContext?.activePeriod) {
         from = parseISO(userContext?.activePeriod?.from);
         to = parseISO(userContext?.activePeriod?.to);
      }

      if (!this.queryParams) {
         await firstValueFrom(this.queryParamsObservable);
      }

      const { path, queryParams } = determineAuditActionEntityPathSegmentAndParameters({
         entityName,
         entityIdOrStandard,
         modelName,
         includeSubsidiaries,
         params: this.queryParams,
         forceAudit,
      });

      const setStatusToUnarchive = (auditValues: any[], _entityName = entityName, approvalStatus: StatusVerificationEnumExtended = 'UNARCHIVE') => {
         return this.#setStatus(approvalStatus, entityIdOrStandard as getIDType<T>, organisationId, from, to, _entityName, auditValues);
      };

      return this.httpClient
         .post(`/${this.version}/${path}/unarchive?${queryParams.toString() ?? ''}`)
         .then((resp) => {
            if (entityName === 'kpifields') {
               void this.refreshCurrentKPIProgress?.();
               return setStatusToUnarchive(resp.data.auditValues);
            } else if (
               [
                  'taxonomy/businessactivities',
                  'taxonomy/minimumsafeguards',
                  'taxonomy/fslis',
                  'taxonomy/climaterisk/analyses',
                  'emissions/ghg/footprintssubs',
                  'emissions/ghg/footprints',
                  'currenciesrates',
               ].includes(entityName) &&
               !modelName
            ) {
               return setStatusToUnarchive(resp.data.auditValues);
            } else if (['kpiValueItem', 'kpivalueitem'].includes(modelName ?? '')) {
               this.#setStatus('UNARCHIVE', entityIdOrStandard as getIDType<T>, organisationId, from, to, modelName, resp.data.auditValues);
               return [];
            } else if (['kpisubs', 'kpivalues'].includes(entityName)) {
               return setStatusToUnarchive(resp.data.auditValues);
            } else if (entityName === 'kpiscopes' && organisationId) {
               return this?.kpiScopesStore?.update(getScopeFilterFunction(from, to, organisationId), {
                  status: ApprovalStatus.APPROVED,
               });
            } else if (entityName === 'scoping/esrs/materialityassessments' && organisationId) {
               this?.kpiScopesStore?.update(getScopeFilterFunction(from, to, organisationId), {
                  status: ApprovalStatus.APPROVED,
               });
            } else if (modelName === 'attachment') {
               return;
            }

            return this.store.update(entityIdOrStandard as getIDType<T>, { status: ApprovalStatus.APPROVED, ...resp.data });
         })
         .catch((error) => this.auditRequestFailed(error));
   }

   buildDataSourcePageQueryParams(params: {
      paginationModel?: GridPaginationModel;
      start?: GridGetRowsParams['start'];
      end?: GridGetRowsParams['end'];
   }): URLSearchParams {
      const urlParams = new URLSearchParams();

      const { start, end } = params;
      // an absent, cursor based or empty range falls back to the page, rather than asking for 0 rows
      const rangeSize = typeof start === 'number' && typeof end === 'number' ? end - start + 1 : 0;

      if (typeof start === 'number' && rangeSize > 0) {
         urlParams.set('offset', String(start));
         urlParams.set('limit', String(rangeSize));
      } else if (params.paginationModel) {
         urlParams.set('limit', String(params.paginationModel.pageSize));
         urlParams.set('page', String((params.paginationModel.page ?? 0) + 1));
      }

      return urlParams;
   }

   async buildDataSourceQueryParams(params: { sortModel: GridSortModel; filterModel: GridFilterModel }): Promise<URLSearchParams> {
      if (!this.queryParams) {
         await firstValueFrom(this.queryParamsObservable);
      }

      const urlParams = new URLSearchParams(this.queryParams);

      (params.filterModel?.items ?? []).forEach(({ field, value }) => {
         if (!isNil(value)) {
            let sanitizedValue = value;

            if (Array.isArray(value)) {
               sanitizedValue = value.join(',');
            } else if (isDate(value)) {
               sanitizedValue = formatISO(value, { representation: 'date' });
            } else if (isObject(value)) {
               sanitizedValue = (value as any)?.id ?? (value as any)?.key ?? (value as any)?.slug ?? JSON.stringify(value);
            }

            urlParams.set(field, sanitizedValue);
         }
      });

      if (Array.isArray(params.filterModel?.quickFilterValues) && (params.filterModel?.quickFilterValues?.length ?? 0) > 0) {
         urlParams.set('text', params.filterModel.quickFilterValues.join(' '));
      }

      if (params.sortModel?.length > 0) {
         urlParams.set('sort', params.sortModel.map(({ field, sort }) => `${field}:${sort ?? 'asc'}`).join(','));
      }

      return urlParams;
   }

   buildDataSource(..._params: Record<string, unknown>[]): GridDataSource {
      return {
         getRows: async (params) => {
            this.store.setLoading(true);

            const generalQueryParams = await this.buildDataSourceQueryParams(params);
            const pageQueryParams = this.buildDataSourcePageQueryParams(params);

            const response = await this.getPage(Number.parseInt(pageQueryParams.get('page') ?? '1', 10), pageQueryParams, generalQueryParams);

            this.store.setLoading(false);

            return {
               rows: Array.isArray(response) ? response : response.data,
               rowCount: Array.isArray(response) ? response.length : response?.total,
            };
         },
         getAggregatedValue: (row, field) => row[field],
         updateRow: async (params) => this.updateEntity(params.rowId as getIDType<T>, diff(params.previousRow, params.updatedRow)),
      };
   }

   async linkAttachment(attachmentId: Attachment['id'], payload: { modelName: string; modelId: number }) {
      return this.httpClient
         .post(`/${this.version}/attachments/${attachmentId}/link`, payload)
         .then((resp) => {
            this.store.update(
               payload.modelId as unknown as UpdateEntityPredicate<getEntityType<T>>,
               ({ attachments = [] }: Partial<getEntityType<T>>) =>
                  ({
                     attachments: arrayAdd(attachments, resp.data),
                  }) as unknown as Partial<getEntityType<T>>
            );

            return resp.data;
         })
         .catch((error) => this.setError(error));
   }

   async unlinkAttachment(attachmentId: number, payload: { modelName: string; modelId: number }) {
      return this.httpClient
         .post(`/${this.version}/attachments/${attachmentId}/unlink`, payload)
         .then(() => {
            this.store.update(
               payload.modelId as unknown as UpdateEntityPredicate<getEntityType<T>>,
               ({ attachments = [] }: Partial<getEntityType<T>>) =>
                  ({
                     attachments: arrayRemove(attachments, attachmentId),
                  }) as unknown as Partial<getEntityType<T>>
            );
            return true;
         })
         .catch((error) => {
            this.setError(error);
            return false;
         });
   }
}

export class SubscriptionsManager {
   private subscriptions = new Map<string | number, Subscription>();

   getAll(): Map<string | number, Subscription> {
      return this.subscriptions;
   }

   add(key: string | number, subscription: Subscription): void {
      if (this.subscriptions.has(key)) {
         this.unsubscribe(key);
      }
      this.subscriptions.set(key, subscription);
   }

   unsubscribe(key: string | number): void {
      const sub = this.subscriptions.get(key);
      if (sub && !sub.closed) {
         sub.unsubscribe();
      }
      this.subscriptions.delete(key);
   }

   has(key: string | number): boolean {
      const subscription = this.subscriptions.get(key);
      return subscription ? !subscription.closed : false;
   }

   unsubscribeAll(): void {
      this.subscriptions.forEach((subscription) => {
         if (!subscription.closed) {
            subscription.unsubscribe();
         }
      });
      this.subscriptions.clear();
   }
}

class RequestsManager {
   private controllers = new Map<string, AbortController>();
   private pendingRequests = new Map<string, Set<string>>();

   constructor(private readonly defaultGroup: string) {}

   add(requestKey: string, group: string = this.defaultGroup): AbortSignal | undefined {
      const pending = this.pendingRequests.get(group) ?? new Set<string>();
      if (pending.has(requestKey)) return undefined; // exact same request already running

      // Abort all existing requests in this group
      const oldCtrl = this.controllers.get(group);
      if (oldCtrl) oldCtrl.abort(`New request in ${group}: ${requestKey}`);

      const controller = new AbortController();
      this.controllers.set(group, controller);

      const updated = new Set<string>();
      updated.add(requestKey);
      this.pendingRequests.set(group, updated);

      return controller.signal;
   }

   remove(requestKey: string, group: string = this.defaultGroup): void {
      const pending = this.pendingRequests.get(group);
      if (!pending) return;

      pending.delete(requestKey);

      if (pending.size === 0) {
         this.pendingRequests.delete(group);
         this.controllers.delete(group);
      }
   }

   abortGroup(group: string = this.defaultGroup, reason?: string): void {
      const ctrl = this.controllers.get(group);
      if (ctrl) ctrl.abort(reason ?? `[${group}]: Abort group`);

      this.controllers.delete(group);
      this.pendingRequests.delete(group);
   }

   abortAll(reason?: string): void {
      for (const [group, ctrl] of this.controllers.entries()) {
         ctrl.abort(reason ?? `[${group}]: Abort all requests`);
      }
      this.controllers.clear();
      this.pendingRequests.clear();
   }

   getSignal(group: string = this.defaultGroup): AbortSignal | undefined {
      return this.controllers.get(group)?.signal;
   }
}

// NOTE: Example for typing a mixin that can be used to add status management to a CRUDService
// Example usage: class MyService extends EntityStatusMixin(CRUDService<MyEntityState>) {}
//
// function EntityStatusMixin<T extends EntityState, BaseClass extends Constructor<CRUDService<T> & WithAbstractProps<T>>>(Base: BaseClass) {
//    return class extends Base {
//       constructor(...args: any[]) {
//          super(...args);
//       }
//       // implement the mixin here
//    };
// }

// function AllowedRolesMixin<T extends EntityState, BaseClass extends Constructor<CRUDService<T>>>(Base: BaseClass) {
//    return class extends Base {
//    // implement the mixin here
//    };
// }

// interface WithAbstractProps<T> {
//    kpiValuesStore: typeof KPIValuesStore;
//    kpiFieldsStore: typeof KPIFieldsStore;
//    kpiScopesStore: typeof KPIScopesStore;
//    refreshCurrentKPIProgress: () => void;
//    setStatus: (
//       organisationId: Organisation['organisationId'],
//       from: Date,
//       to: Date,
//       setStatusValues: SetStatusValues,
//       auditValues: getIDType<T>[]
//    ) => void;
//    setStatusApprovalCenter: (setStatusValues: SetStatusValues, auditValues: getIDType<T>[], entityName?: string) => void;
// }
